import { stringify } from "querystring";

import {
  FormType,
  FrontendRoutes,
  DonationFormCategory,
  TicketingFormCategory,
  IsoLocale,
  getDonationOrTicketingFormLink,
  FormCategory,
  Emptyable,
} from "@simplyk/common";
import { inferRouterOutputs } from "@trpc/server";

import { Event } from "../components/PaymentProcessor/type";
import { ThankYouPageQueryParams } from "../components/ThankYouPage/type";
import { GetCurrentUserFrontendObject } from "../contexts/types";

import { AppRouterType } from "./trpc";

import { DonationFormFrontendPageOutput, TicketingFrontendPageOutput } from "@/types/trpc";

type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

export interface ThankYouUrlParams {
  command: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
  formType: FormType;
  organization: NonNullable<GetCurrentUserFrontendObject["currentOrganization"]>;
  postTransactionUrl: string | null | undefined;
  isEmbeddedModal: boolean;
  generateETicket: boolean;
  event?: Event;
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  isoLocale: IsoLocale;
  category: DonationFormCategory | TicketingFormCategory;
  isAuction: boolean;
  themeColor?: Emptyable<string>;
  isDarkMode: boolean;
  hasShapes: boolean;
}

export const getThankYouPageUrl = ({
  command,
  formType,
  organization,
  postTransactionUrl,
  isEmbeddedModal,
  generateETicket,
  event,
  formData,
  isoLocale,
  category,
  themeColor,
  isDarkMode,
  hasShapes,
}: ThankYouUrlParams): string => {
  const params: ThankYouPageQueryParams & Record<string, string | null | undefined | number> = {
    organizationName: organization?.name || "",
    logoUrl: formData.logoUrl || organization?.logoUrl || null,
    amount: command.totalAmount - command.tipAmount,
    tipAmount: command.tipAmount,
    formCategory: category || null,
    modal: isEmbeddedModal ? "true" : "false",
    email: command.email || null,
    formType,
    paymentMethod: command.paymentMethod || null,
    shareLink:
      process.env.NEXT_PUBLIC_FRONTEND_URL +
      getDonationOrTicketingFormLink({
        form: {
          formCategory: category as FormCategory,
          formType,
          id: formData.id,
          path: formData.path,
          isPrimaryCampaign: (formData as DonationFormFrontendPageOutput).isPrimaryCampaign || false,
          isPrimaryTeam: (formData as DonationFormFrontendPageOutput).isPrimaryTeam || false,
        },
        isoLocale,
      }),
    generateTaxReceipt: getGenerateTaxReceipt(formType, formData, organization, command) ? "true" : "false",
    generateETicket: generateETicket && (command.productsAmount || 0) > 0 ? "true" : "false",
    resourceEventEndTime: event?.endTime || null,
    themeColor,
    isDarkMode: isDarkMode ? "true" : "false",
    hasShapes: hasShapes ? "true" : "false",
  };

  return postTransactionUrl || `/${isoLocale}${FrontendRoutes.ThankYou}?${stringify(params)}`;
};

const getGenerateTaxReceipt = (
  formType: FormType,
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput,
  organization: NonNullable<GetCurrentUserFrontendObject["currentOrganization"]>,
  command: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"]
) => {
  if (formType === FormType.DonationForm && !(formData as DonationFormFrontendPageOutput).hasReceipt) {
    return false;
  }

  return (command.eligibleAmount || 0) > 0 && organization.canGenerateReceipt;
};
